import React, { useRef, useEffect, useCallback, useState } from "react";
import { Box } from "@material-ui/core";
import clsx from "clsx";
import NoteItem from "../NoteItem/NoteItem";
import useNotesContext from "../../../hooks/useNotesContext";

const NotesList = ({ notes }) => {
  const { deleteNote } = useNotesContext();
  const prevOrderRef = useRef([]);
  const itemRefs = useRef({});
  const containerRef = useRef(null);
  const [deletingId, setDeletingId] = useState(null);
  const animatingCount = useRef(0);

  const lockOverflow = () => {
    animatingCount.current++;
    if (containerRef.current) {
      containerRef.current.style.overflowY = "hidden";
    }
  };

  const unlockOverflow = () => {
    animatingCount.current = Math.max(0, animatingCount.current - 1);
    if (animatingCount.current === 0 && containerRef.current) {
      containerRef.current.style.overflowY = "auto";
    }
  };

  const setItemRef = useCallback((noteID, el) => {
    if (el) {
      itemRefs.current[noteID] = el;
    } else {
      delete itemRefs.current[noteID];
    }
  }, []);

  const handleDeleteRequest = useCallback((noteID) => {
    setDeletingId(noteID);
  }, []);

  const handleFadeEnd = useCallback((noteID) => {
    if (noteID === deletingId) {
      setDeletingId(null);
      deleteNote(noteID);
    }
  }, [deletingId, deleteNote]);

  const prefersReducedMotion =
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  useEffect(() => {
    const prevOrder = prevOrderRef.current;
    const currentOrder = notes.map((n) => n.noteID);

    if (prevOrder.length > 0 && !prefersReducedMotion) {
      let hasAnimation = false;
      currentOrder.forEach((noteID, newIndex) => {
        const oldIndex = prevOrder.indexOf(noteID);
        if (oldIndex !== -1 && oldIndex !== newIndex) {
          const el = itemRefs.current[noteID];
          if (el) {
            if (!hasAnimation) {
              lockOverflow();
              hasAnimation = true;
            }
            const delta = (oldIndex - newIndex) * el.offsetHeight;
            el.style.transition = "none";
            el.style.transform = `translateY(${delta}px)`;

            requestAnimationFrame(() => {
              requestAnimationFrame(() => {
                el.style.transition = "transform 0.35s ease";
                el.style.transform = "translateY(0)";
                const onEnd = () => {
                  el.removeEventListener("transitionend", onEnd);
                  unlockOverflow();
                };
                el.addEventListener("transitionend", onEnd);
              });
            });
          }
        }
      });
    }

    prevOrderRef.current = currentOrder;
  }, [notes]);

  return (
    <Box ref={containerRef} style={{ overflowY: "auto", flex: 1 }}>
      {notes.map((note, index) => (
        <div
          key={note.noteID}
          ref={(el) => setItemRef(note.noteID, el)}
          data-id={`note-item-${index}`}
          className={clsx(
            deletingId === note.noteID && "animate__animated animate__fadeOut"
          )}
          style={{
            "--animate-duration": "0.25s",
            backfaceVisibility: "hidden",
          }}
          onAnimationEnd={() => {
            if (deletingId === note.noteID) {
              handleFadeEnd(note.noteID);
            }
          }}
        >
          <NoteItem note={note} onDeleteRequest={handleDeleteRequest} />
        </div>
      ))}
    </Box>
  );
};

export default NotesList;
